import { Injectable, PLATFORM_ID, WritableSignal, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, finalize, map } from 'rxjs';
import { GiftCardService } from '../../pages/gift-cards/gift-cards.service';
import { ApiResponse } from '../common/types';
import { HTTP_STATUS_CODES } from '../constants/errors';

/** What the backend answers when the purchase carries no proof of the sender's address. */
export const EMAIL_VERIFICATION_REQUIRED_CODE = 'email_verification_required';

export const VERIFICATION_CODE_LENGTH = 6;

const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Email verification by code for the gift-card purchase without a session (DEV-11124 / ELIO-6959).
 *
 * The other half of the anti-carding work: a card tester holding no account paid through this
 * form, so the backend now refuses to charge until the sender proves the address is theirs.
 *
 * Why a code and not a link, which is what the signup uses: the buyer never leaves the tab. A
 * link would break the case of opening the mail on the phone while the purchase sits on the
 * desktop — there is no tab to come back to.
 *
 * The step is driven by the backend, not by a flag in the front: the purchase is attempted, and
 * only a rejection carrying `email_verification_required` opens it. That way the same build works
 * against a backend with the check off (`gift_card_email_verification_required = 0`) without
 * asking the customer for a code nobody is going to validate.
 *
 * Kept in root on purpose: the proof is good for 30 minutes and survives a declined card, so
 * closing and reopening the purchase modal must not cost the customer a second code.
 *
 * CONTRACT — the deliverables note from the backend names the two endpoints, the payload field
 * and the rejection code; the exact request and response shapes are the assumption below, to be
 * confirmed against `smc_api_front/docs/DEV-11124-INTEGRACION_FRONT.md`. Everything that would
 * change lives here and in `GiftCardService`.
 */
@Injectable({ providedIn: 'root' })
export class CodeVerificationService {

  #giftCardService = inject(GiftCardService);
  #platformId = inject(PLATFORM_ID);

  /** The purchase is parked on the code screen. */
  pending = signal(false);

  isSending = signal(false);

  isChecking = signal(false);

  resendCooldown = signal(0);

  /** Shown under the input: wrong code, expired, or too many tries. */
  error: WritableSignal<string | null> = signal(null);

  /** Address the code went to. Normalized the way the backend normalizes it. */
  email = signal('');

  /**
   * Proof that the sender owns the address: good for 30 minutes, reusable while a card is
   * being declined, and consumed by the backend when the purchase finally goes through.
   */
  #token: string | null = null;

  #cooldownIntervalId: any = null;

  /**
   * Opens the step and sends the first code. Whatever proof was held is dropped: the backend
   * just refused it, so it is spent or expired.
   */
  start(email: string): void {
    const normalized = this.#normalize(email);
    if (!normalized) return;

    const sameAddress = this.email() === normalized;

    this.#token = null;
    this.email.set(normalized);
    this.error.set(null);
    this.pending.set(true);

    // Coming back to a code that is still in the customer's inbox — they closed the purchase and
    // reopened it — must not spend one of the few codes a day the backend allows.
    if (sameAddress && this.resendCooldown() > 0) return;

    this.#send(true);
  }

  /**
   * The customer left the purchase. The screen goes away, but the address and the cooldown stay:
   * the code already sent is still valid, so reopening should ask for it, not send another.
   */
  dismiss(): void {
    this.pending.set(false);
  }

  resend(): void {
    this.#send(false);
  }

  /** Trades the code for the purchase token. Emits false and leaves `error` set on a rejection. */
  check(code: string): Observable<boolean> {
    this.isChecking.set(true);
    this.error.set(null);

    return this.#giftCardService
      .checkVerificationCode(this.email(), code)
      .pipe(
        finalize(() => this.isChecking.set(false)),
        map((res: ApiResponse<any>) => {
          if (res?.error) {
            this.error.set(res.message || 'That code is not valid. Please check it and try again.');
            return false;
          }

          const token = this.#readToken(res);
          if (!token) {
            // The code was accepted but the proof is not where we read it: a contract mismatch,
            // and nothing the customer can fix by typing the code again. Their own message would
            // say «Email verified» here, which is the last thing to show in red.
            this.error.set('We could not complete the verification. Please try again in a moment.');
            return false;
          }

          this.#token = token;
          this.error.set(null);
          this.pending.set(false);
          this.#stopCooldown();
          return true;
        })
      );
  }

  /**
   * The proof, but only for the address it was issued to: editing the sender's email between
   * attempts has to send the customer back for a new code, not reuse someone else's.
   */
  tokenFor(email: string): string | undefined {
    return this.#token && this.email() === this.#normalize(email) ? this.#token : undefined;
  }

  /** The purchase went through, so the backend consumed the token. */
  clear(): void {
    this.#token = null;
    this.email.set('');
    this.pending.set(false);
    this.error.set(null);
    this.#stopCooldown();
    this.resendCooldown.set(0);
  }

  #send(isFirstSend: boolean): void {
    if (this.isSending() || (!isFirstSend && this.resendCooldown() > 0) || !this.email()) return;

    this.isSending.set(true);

    this.#giftCardService
      .sendVerificationCode(this.email())
      .pipe(finalize(() => this.isSending.set(false)))
      .subscribe((res: ApiResponse<any>) => {
        if (res?.error) {
          this.error.set(res.message || 'We could not send the code. Please try again in a moment.');
          // A refused resend still has to hold the button down, or the customer keeps hitting
          // the daily cap with no idea why.
          this.#startCooldown(this.#retryAfter(res));
          return;
        }

        this.error.set(null);
        this.#startCooldown(RESEND_COOLDOWN_SECONDS);
      });
  }

  #readToken(res: ApiResponse<any>): string | null {
    // The backend answers either raw or inside the standard envelope, as in the signup flow.
    // It hands the proof back as `verificationToken` — confirmed against dev — while the
    // purchase payload carries the same value under `emailVerificationToken`.
    const data = res?.data ?? res;
    return data?.verificationToken ?? data?.emailVerificationToken ?? data?.token ?? null;
  }

  #retryAfter(res: ApiResponse<any>): number {
    if (res?.status !== HTTP_STATUS_CODES.TOO_MANY_REQUESTS) return RESEND_COOLDOWN_SECONDS;
    // Top level is where the backend puts it — `429 attempt_limit` on the card endpoints answers
    // `{ message, code, retryAfter }` — with the envelope kept as a fallback.
    const retryAfter = Number(res?.retryAfter ?? res?.data?.retryAfter);
    return Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : RESEND_COOLDOWN_SECONDS;
  }

  #normalize(email: string): string {
    return (email || '').trim().toLowerCase();
  }

  #startCooldown(seconds: number): void {
    if (!isPlatformBrowser(this.#platformId)) return;

    this.#stopCooldown();
    this.resendCooldown.set(seconds);
    this.#cooldownIntervalId = setInterval(() => {
      const remaining = this.resendCooldown() - 1;
      this.resendCooldown.set(Math.max(0, remaining));
      if (remaining <= 0) this.#stopCooldown();
    }, 1000);
  }

  #stopCooldown(): void {
    clearInterval(this.#cooldownIntervalId);
    this.#cooldownIntervalId = null;
  }
}
